import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "Why Us" },
    { to: "/blog", label: "Resources" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
                scrolled
                    ? "bg-cream/85 backdrop-blur-xl border-b border-linec shadow-[0_1px_20px_rgba(32,58,46,0.06)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <nav className="max-w-[1240px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-2.5 group"
                >
                    <img
                        src="/logo.png"
                        alt="DentAssists logo"
                        className="w-10 h-10 rounded-full shadow-sm transition-transform duration-200 group-hover:scale-105"
                    />
                    <span className="font-heading text-xl tracking-tight text-ink">
                        DentAssists
                    </span>
                </Link>

                <div className="hidden md:flex items-center gap-9">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm tracking-tight transition-colors duration-200 hover:text-sage ${
                                    isActive ? "text-sage font-medium" : "text-inksoft"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-cta-button"
                        className="text-sm font-medium bg-terracotta text-cream px-5 py-2.5 rounded-sm transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5"
                    >
                        Free Consultation
                    </Link>
                </div>

                <button
                    className="md:hidden text-ink p-2"
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </nav>

            {open && (
                <div className="md:hidden bg-cream border-b border-linec px-6 py-6 flex flex-col gap-5">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            className="text-base text-ink"
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        className="text-center text-sm font-medium bg-terracotta text-cream px-5 py-3 rounded-sm"
                        data-testid="mobile-nav-cta"
                    >
                        Free Consultation
                    </Link>
                </div>
            )}
        </header>
    );
}
